/**
 * The whole inventory, on one page.
 *
 * The desk answers "what is wrong today". This answers "what do we have" —
 * every line, every store, every bin, with what it is worth and what it is
 * doing, and nothing filtered away. It is the page a storekeeper prints for a
 * count, hands to an auditor, or scrolls looking for the thing they know is
 * on a shelf somewhere.
 *
 * Two rules it keeps that a stock list usually does not:
 *
 *  - **Every bin is shown, not a total.** A line held in three stores is three
 *    facts, and the single number that sums them is the one thing nobody can
 *    act on.
 *  - **Nothing is hidden by default.** Obsolete lines, lines with no cost,
 *    lines nobody has ever drawn and lines that have been taken off the
 *    working catalogue are all here, marked. A register that quietly omits its
 *    awkward rows is how a store comes to believe its own summary.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import {
  categoriesOf,
  categoryLabel,
  heldAt,
  onHand,
  rows as stockRows,
  STATE_LABEL,
  type StockRow,
} from "../../domain/stores";
import { Card, EmptyState, Field, Input, Select, Table } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { x as ex } from "../executive/shared";
import { useStores } from "./useStores";
import { t, tn } from "../../i18n";
import s from "./Stores.module.css";

type Group = "none" | "category" | "store" | "supplier";

export function InventoryPage() {
  const session = useSession();
  const entered = useStores();
  const register = entered.value;
  const now = new Date();

  const [group, setGroup] = useState<Group>("category");
  const [find, setFind] = useState("");
  const [store, setStore] = useState("all");

  const all = useMemo(() => stockRows(register, now), [register]);
  const retired = useMemo(
    () => register.items.filter((i) => i.obsoleted),
    [register],
  );

  const shown = useMemo(() => {
    const needle = find.trim().toLowerCase();
    return all.filter(
      (r) =>
        (store === "all" || r.item.bins.some((b) => b.location === store)) &&
        (!needle ||
          r.item.sku.toLowerCase().includes(needle) ||
          r.item.name.toLowerCase().includes(needle) ||
          (r.item.partNumber ?? "").toLowerCase().includes(needle) ||
          (r.item.manufacturer ?? "").toLowerCase().includes(needle) ||
          (r.item.supplier ?? "").toLowerCase().includes(needle)),
    );
  }, [all, find, store]);

  const groups = useMemo(() => {
    if (group === "none") return [{ key: "all", label: t("Every line"), rows: shown }];
    const by = new Map<string, StockRow[]>();
    for (const r of shown) {
      const keys =
        group === "category"
          ? [categoryLabel(r.item.category, register)]
          : group === "supplier"
            ? [r.item.supplier ?? t("No supplier recorded")]
            : [...new Set(r.item.bins.map((b) => b.location))].map(
                (code) => register.locations.find((l) => l.code === code)?.name ?? code,
              );
      for (const k of keys.length ? keys : [t("Nowhere recorded")]) {
        by.set(k, [...(by.get(k) ?? []), r]);
      }
    }
    return [...by.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([label, list]) => ({ key: label, label, rows: list }));
  }, [shown, group, register]);

  const value = shown.map((r) => r.value).filter((v): v is number => v !== null);
  const noCost = shown.length - value.length;
  const units = shown.reduce((n, r) => n + r.onHand, 0);

  return (
    <Page
      eyebrow={t("{site} · Stores", { site: session.site.code })}
      title="The whole inventory"
      actions={
        <div className={s.actions}>
          <Link to="/stores" className={ex.tag}>
            {t("Back to the desk")}
          </Link>
          <button type="button" className={ex.tag} onClick={() => window.print()}>
            {t("Print")}
          </button>
        </div>
      }
    >
      <p className={s.lede}>
        {t(
          "Every line in every store and bin, unfiltered. Lines with no cost, never drawn, or taken off the catalog are included and marked.",
        )}
      </p>

      <div className={s.chips} data-testid="inventory-totals">
        <span className={s.chip}>
          <b>{shown.length}</b> {tn(shown.length, "line", "lines")}
        </span>
        <span className={s.chip}>
          <b>{units.toLocaleString()}</b> {t("units on the shelves")}
        </span>
        <span className={s.chip}>
          <b>{value.length ? Math.round(value.reduce((a, b) => a + b, 0)).toLocaleString() : "—"}</b> {register.currency}
          {noCost > 0 && <span className={s.why}> · {t("{n} carry no cost", { n: noCost })}</span>}
        </span>
        <span className={s.chip}>
          <b>{register.locations.length}</b> {tn(register.locations.length, "store", "stores")}
        </span>
        <span className={s.chip}>
          <b>{categoriesOf(register).length}</b> {t("categories")}
        </span>
      </div>

      <div className={s.filters}>
        <Field label={t("Find")}>
          <Input
            value={find}
            onChange={(e) => setFind(e.target.value)}
            placeholder={t("a number, a name, a maker, a supplier")}
            aria-label={t("Find")}
            data-testid="inventory-find"
          />
        </Field>
        <Field label={t("Store")}>
          <Select value={store} onChange={(e) => setStore(e.target.value)} aria-label={t("Store")}>
            <option value="all">{t("Every store")}</option>
            {register.locations.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("Grouped by")}>
          <Select value={group} onChange={(e) => setGroup(e.target.value as Group)} aria-label={t("Grouped by")} data-testid="inventory-group">
            <option value="category">{t("Category")}</option>
            <option value="store">{t("Store")}</option>
            <option value="supplier">{t("Supplier")}</option>
            <option value="none">{t("Not grouped")}</option>
          </Select>
        </Field>
      </div>

      {shown.length === 0 ? (
        <EmptyState>{t("Nothing matches that.")}</EmptyState>
      ) : (
        groups.map((g) => (
          <Card key={g.key} title={g.label} sub={tn(g.rows.length, "{n} line", "{n} lines")}>
            <Table>
              <thead>
                <tr>
                  <th>{t("Line")}</th>
                  <th>{t("Where it is")}</th>
                  <th className={s.num}>{t("On hand")}</th>
                  <th className={s.num}>{t("Reserved")}</th>
                  <th className={s.num}>{t("Available")}</th>
                  <th className={s.num}>{t("Reorder point / maximum")}</th>
                  <th className={s.num}>{t("Used in the year")}</th>
                  <th className={s.num}>{t("Unit cost")}</th>
                  <th className={s.num}>{t("Value")}</th>
                  <th>{t("State")}</th>
                </tr>
              </thead>
              <tbody data-testid="inventory-rows">
                {g.rows.map((r) => (
                  <tr key={`${g.key}-${r.item.sku}`}>
                    <td className={s.name}>
                      <Link to={`/stores/${encodeURIComponent(r.item.sku)}`}>{r.item.name}</Link>
                      {r.item.critical && <span className={s.critical}>{t("CRITICAL")}</span>}
                      <div className={s.sku}>
                        {r.item.sku}
                        {r.item.partNumber && ` · ${r.item.partNumber}`}
                        {r.item.manufacturer && ` · ${r.item.manufacturer}`}
                      </div>
                    </td>
                    <td className={s.serves}>
                      {/* every bin, because a line in three stores is three facts */}
                      {r.item.bins.length === 0 ? (
                        <span className={s.unknown}>{t("no bin recorded")}</span>
                      ) : (
                        r.item.bins.map((b, i) => (
                          <div key={`${b.location}-${b.bin ?? i}`}>
                            {register.locations.find((l) => l.code === b.location)?.name ?? b.location}
                            {b.bin && ` · ${b.bin}`} — {b.onHand} {r.item.uom}
                          </div>
                        ))
                      )}
                    </td>
                    <td className={s.num}>{`${r.onHand} ${r.item.uom}`}</td>
                    <td className={s.num}>{r.reserved || <span className={s.unknown}>—</span>}</td>
                    <td className={`${s.num} ${r.available <= 0 ? s.bad : ""}`}>{r.available}</td>
                    <td className={s.num}>{`${r.item.min} / ${r.item.max}`}</td>
                    <td className={s.num}>
                      {r.consumed365}
                      {r.obsolete && <div className={s.why}>{t("nothing in a year")}</div>}
                    </td>
                    <td className={s.num}>
                      {r.item.unitCost === undefined ? (
                        <span className={s.unknown}>{t("none")}</span>
                      ) : (
                        r.item.unitCost.toLocaleString()
                      )}
                    </td>
                    <td className={s.num}>
                      {r.value === null ? <span className={s.unknown}>—</span> : Math.round(r.value).toLocaleString()}
                    </td>
                    <td>
                      <span className={s.state} data-state={r.state}>
                        {t(STATE_LABEL[r.state])}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        ))
      )}

      {retired.length > 0 && (
        <Card title="Taken off the working catalog" sub="kept with their movements; a catalog number is never reused">
          <Table>
            <thead>
              <tr>
                <th>{t("Line")}</th>
                <th className={s.num}>{t("On hand")}</th>
                <th>{t("When")}</th>
                <th>{t("Why")}</th>
              </tr>
            </thead>
            <tbody>
              {retired.map((i) => (
                <tr key={i.sku}>
                  <td>
                    {i.name}
                    <div className={s.sku}>{i.sku}</div>
                  </td>
                  <td className={s.num}>{onHand(i)}</td>
                  <td className={s.when}>{i.obsoleted?.at.slice(0, 10)}</td>
                  <td className={s.why}>{i.obsoleted?.reason}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      <Card title="By store" sub="what each store holds, and what it is worth">
        <Table>
          <thead>
            <tr>
              <th>{t("Store")}</th>
              <th>{t("Held by")}</th>
              <th className={s.num}>{t("Lines")}</th>
              <th className={s.num}>{t("Units")}</th>
              <th className={s.num}>{t("Value")}</th>
            </tr>
          </thead>
          <tbody data-testid="inventory-by-store">
            {register.locations.map((l) => {
              const here = all.filter((r) => r.item.bins.some((b) => b.location === l.code));
              const u = here.reduce((n, r) => n + heldAt(r.item, l.code), 0);
              const costs = here.map((r) => (r.item.unitCost === undefined ? null : heldAt(r.item, l.code) * r.item.unitCost));
              const known = costs.filter((c): c is number => c !== null);
              return (
                <tr key={l.code}>
                  <td>
                    {l.name}
                    <div className={s.sku}>{l.code}</div>
                  </td>
                  <td className={s.why}>{l.custodian ?? t("nobody recorded")}</td>
                  <td className={s.num}>{here.length}</td>
                  <td className={s.num}>{u.toLocaleString()}</td>
                  <td className={s.num}>
                    {known.length === costs.length
                      ? `${Math.round(known.reduce((a, b) => a + b, 0)).toLocaleString()} ${register.currency}`
                      : t("{n} of {total} priced", { n: known.length, total: costs.length })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>
    </Page>
  );
}
